import { useState } from "react";
import { Button, Input, Textarea } from "@gradebook/ui";
import { sendCrashFeedback } from "@/lib/sentry";

/**
 * "What were you doing?", attached to one specific crash.
 *
 * Name and email are asked for rather than taken from the session on purpose:
 * the app never puts a person's name or address into Sentry by itself (see
 * `setSentryUser`), so the only ones that go are the ones someone chose to type
 * — and they are optional.
 */
export function CrashReportForm({ eventId }: { eventId: string }) {
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  if (sent) {
    return (
      <p className="mt-6 border-t pt-4 text-center text-sm text-muted-foreground">
        Thank you — your report has been sent to the developers.
      </p>
    );
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!message.trim()) return;

    sendCrashFeedback({
      message: message.trim(),
      name: name.trim() || undefined,
      email: email.trim() || undefined,
      associatedEventId: eventId,
    });
    setSent(true);
  };

  return (
    <form onSubmit={handleSubmit} className="mt-6 border-t pt-4 text-left">
      <label
        htmlFor="crash-report-message"
        className="mb-1 block text-sm font-medium text-foreground"
      >
        What were you doing when this happened?
      </label>
      <p className="mb-2 text-xs text-muted-foreground">
        Optional, and it helps a great deal — only you can say what you
        expected to happen.
      </p>
      <Textarea
        id="crash-report-message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={3}
        placeholder="I clicked Save on the term grades for 11B and the page went blank."
      />

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Input
          label="Your name"
          placeholder="Optional"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          label="Email"
          type="email"
          placeholder="Optional"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        {/* Shown so a person can quote it to an administrator, and so the same
            crash can be found from a support conversation. */}
        <span
          className="font-mono text-[11px] text-muted-foreground"
          title="Reference for this error"
        >
          {eventId.slice(0, 8)}
        </span>
        <Button type="submit" size="sm" disabled={!message.trim()}>
          Send report
        </Button>
      </div>
    </form>
  );
}
